import mainSocket from '../../../../shared/services/mainSocketClass';
import cache from '../models/cache-model';

import {
    RACES_TYPE_MAP,
} from '../../constants';

import {
    fetchInitialData,
    fetchScheduleUpdate,
    fetchRacecardData,
    getRaceType,
    getSportSlug,
} from '../../utils';

import {
    racingLoading,
    loadSuccess,
    hasErrored,
    scheduleIsEmpty,
} from './loadingActions';

import {
    racingInit,
    updateSchedule,
    updateScheduleFromCache,
    invalidateCache,
    subscribeWsEventUpdates,
    unsubscribeWsEventUpdates,
} from './scheduleActions';

import {
    updateRaceCard,
    updateRaceCardFromCache,
    updatePrice,
    setStreaming,
} from './racingActions';

import {
    getRacingWidgetJitter,
} from '~spa/Utils/SiteConfig';

import {
    FEATURED_PAGE,
    NEXT_RACES_PAGE,
} from '~scenes/racingwidget/constants';

import { isIndividualPage } from '~scenes/sport/horse-racing/utils';

let wsEventHandlerCallbackQueue = [];

/**
 * WS Event subscribers
 *
 * @param {Func} dispatch Action dispatcher
 * @param {String} type Type of race
 * @param {Func} getState getState redux func
 * @param {Boolean} doNotGetSelsForHomePage do not retrieve the selections for home page widget use - the
 * racing 'next races' and 'featured' components retrieve a full racecard instead, making these redundant
 * @returns {void}
 */
const eventSubscriber = (dispatch, type, getState, doNotGetSelsForHomePage = false) => {
    return (wsData) => {
        eventOffSubscriber(dispatch, type, wsData, getState, doNotGetSelsForHomePage);
        priceUpdateDispatcher(wsData, dispatch);
    };
};


/**
 * Price update dispatcher
 *
 * @param {Object} data WS Data
 * @param {Func} dispatch Action dispatcher
 *
 * @returns {void}
 */
const priceUpdateDispatcher = (data, dispatch) => {
    if (data.action === 'price_update') {
        dispatch(updatePrice(data));
    }
};


/**
 *  Init data for racing
 *  racing widget is used on 3 pages:
 *      1.homepage-featured widget
 *      2.racingpage-featured page
 *      3.racingpage-nextraces page
 *
 * @param {String} type sport type
 * @param {String} displayType display type - eg. 'featured-page', 'next-races-page'
 * @param {Object} apiData when component is used for an individual page
 * it will receive data prop from higher order component
 * @param {Function} loadedCallback callback called after loading has finished
 * @returns {Func} async func
 */
export const initRacing = (type, displayType, { lazyLoadHasLoaded, lazyLoadIsEmpty }) => {
    return async (dispatch, getState) => {
        let data;
        switch (displayType) {
            case FEATURED_PAGE:
                dispatch(racingLoading());
                data = await fetchScheduleUpdate(type, 'featured', true);
                break;
            case NEXT_RACES_PAGE:
                dispatch(racingLoading());
                data = await fetchScheduleUpdate(type, 'next-races', true);
                break;
            default:
                data = await fetchInitialData(type);
        }

        const doNotGetSelsForHomePage = isIndividualPage(displayType);

        if ('events' in data) {
            dispatch([
                racingInit(data),
                subscribeWsEventUpdates(data.events),
                loadSuccess(),
            ]);

            const wsEventHandlerCallback = mainSocket.onWsEventUpdate(eventSubscriber(dispatch, type, getState, doNotGetSelsForHomePage));
            wsEventHandlerCallbackQueue.push(wsEventHandlerCallback);
            lazyLoadHasLoaded();
        } else {
            dispatch(scheduleIsEmpty());
            lazyLoadIsEmpty();
        }
    };
};

/**
 * Get schedule for selected type tab
 *
 * @param {String} type sport type
 * @param {Number} tabIdx index of the type tab
 * @returns {Func} async func
 */
export const getSchedule = (type, tabIdx) => {
    const raceType = RACES_TYPE_MAP.get(tabIdx);

    return async (dispatch, getState) => {
        if (raceType !== getRaceType(getState().featured)) {
            const sportSlug = getSportSlug(getState().sports_details);
            dispatch(racingLoading());
            if (cache.hasCache(sportSlug, raceType)) {
                const cachedEvents = cache.getCacheByType(sportSlug, raceType);
                dispatch(updateScheduleFromCache(cachedEvents, raceType));
            }
            await dispatchScheduleUpdate(dispatch, type, raceType, getState);
        }
    };
};

/**
 * Fetches new selections and fires race card action
 *
 * @param {Number} eventId Event id
 * @returns {Func} async func
 */
export const getRaceCard = eventId => {
    return async (dispatch, getState) => {
        const raceType = getRaceType(getState().featured);
        const sportSlug = getSportSlug(getState().sports_details);

        if (cache.hasSelections(sportSlug, raceType, eventId)) {
            const cachedEvents = cache.getCacheByType(sportSlug, raceType);
            dispatch(updateRaceCardFromCache({
                type_code: cachedEvents.type_code[eventId],
                selections: cachedEvents.selections[eventId],
                timeform_events: cachedEvents.timeform_events,
                timeform_selections: cachedEvents.timeform_selections[eventId],
                selected_event_id: eventId,
            }));
        } else {
            const raceCardData = await fetchRacecardData(eventId);
            if (Object.keys(raceCardData).length) {
                dispatch(updateRaceCard(raceCardData, eventId));
            } else {
                dispatch(updateRaceCard({
                    selections: [],
                }, eventId));
            }
        }
    };
};

/**
 * Private helper function to reuse schedule updates
 *
 * @param {*} dispatch Dispatch
 * @param {*} type widget type
 * @param {*} raceType race type
 * @param {Function} getState redux state function
 * @param {Boolean} doNotGetSelsForHomePage do not retrieve the selections for home page widget use - the
 * racing 'next races' and 'featured' components retrieve a full racecard instead, making these redundant
 * @returns {undefined}
 */
const dispatchScheduleUpdate = async (dispatch, type, raceType, getState, doNotGetSelsForHomePage = false) => {
    const data = await fetchScheduleUpdate(type, raceType, doNotGetSelsForHomePage);
    if ('events' in data) {
        dispatch([
            updateSchedule(data, type),
            subscribeWsEventUpdates(data.events),
            loadSuccess(),
        ]);
        const wsEventHandlerCallback = mainSocket.onWsEventUpdate(eventSubscriber(dispatch, type, getState, doNotGetSelsForHomePage));
        wsEventHandlerCallbackQueue.push(wsEventHandlerCallback);
    } else {
        dispatch([
            hasErrored(),
            loadSuccess(),
        ]);
    }
};

/**
 * unmount hook for racing
 * it unregisters the ws events
 *
 * @returns {Func} async func
 */
export const unregisterWsUpdate = () => {
    return (dispatch, getState) => {
        const {featured, init} = getState();
        if (init) {
            const raceType = getRaceType(featured);
            dispatch(unsubscribeWsEventUpdates(raceType));
            wsEventHandlerCallbackQueue.forEach(cb => cb());
            wsEventHandlerCallbackQueue = [];
        }
    };
};

/**
 * Subscriber to update events after
 * receiving ws event off action
 *
 * @param {Func} dispatch Dispatch
 * @param {String} type widget type
 * @param {Object} wsData websocket data
 * @param {Function} getState redux state function
 * @param {Boolean} doNotGetSelsForHomePage do not retrieve the selections for home page widget use - the
 * racing 'next races' and 'featured' components retrieve a full racecard instead, making these redundant
 * @returns {undefined}
 */
const eventOffSubscriber = (dispatch, type, wsData, getState, doNotGetSelsForHomePage = false) => {
    if (wsData.action === 'event_off' && !getState().isStreaming) {
        const raceType = getRaceType(getState().featured);
        // lookup in cache/invalidate cache
        // reload data if offed event is inside active tab
        wsData.events.forEach(eventOffId => {
            const cacheRaceTypes = Object.keys(
                cache.getSportCache(type)
            );
            for (const raceTypeKey of cacheRaceTypes) {
                if (cache.hasCache(type, raceTypeKey, eventOffId)) {
                    dispatch(unsubscribeWsEventUpdates(raceTypeKey));
                    dispatch(invalidateCache(raceTypeKey));
                    if (raceType === raceTypeKey) {
                        dispatch(racingLoading());
                        /**
                         * AM-2522 - quick and dirty mitigation. A proper solution requires some investigation and may
                         * have UX implications. After AM-2471 this should not be as big a problem (as we now prevent
                         * duplicated wsHandlers) from being created.
                         * @todo AM-2540 Investigate a proper solution
                         */

                        const jitter = Math.floor(Math.random() * getRacingWidgetJitter() * 1000);
                        setTimeout(
                            () => dispatchScheduleUpdate(dispatch, type, raceType, getState, doNotGetSelsForHomePage),
                            jitter
                        );
                    }
                }
            }
        });
    }
};

/**
 * set isStreaming
 * @param {Boolean} isStreaming isStreaming
 * @returns {Func} async func
 */
export const setIsStreaming = isStreaming => {

    return async (dispatch) => {
        await dispatch(setStreaming(isStreaming));
    };
};
